import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Tenant } from './useTenants';

/**
 * Hook to sync tenant selection with URL parameter
 * Always adds ?tenant=<slug> when switching tenants
 */
export function useTenantURLSync(
  tenants: Tenant[],
  currentTenant: Tenant | null,
  setCurrentTenant: (tenant: Tenant) => void
) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read tenant from URL on mount
  useEffect(() => {
    const tenantSlug = searchParams.get('tenant');

    if (tenantSlug && tenants.length > 0) {
      // Find tenant by slug
      const tenant = tenants.find(t => t.slug === tenantSlug);

      if (tenant && (!currentTenant || currentTenant.slug !== tenantSlug)) {
        // Switch to tenant from URL
        setCurrentTenant(tenant);
      } else if (!tenant) {
        // Invalid slug in URL - remove it
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('tenant');
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  }, [searchParams, tenants, currentTenant, setCurrentTenant, setSearchParams]);

  // Update URL when tenant changes
  const updateTenantInURL = useCallback((tenant: Tenant) => {
    const newSearchParams = new URLSearchParams(searchParams);

    // Always set the tenant slug in URL for explicit tenant switching
    if (tenant.slug) {
      newSearchParams.set('tenant', tenant.slug);
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return {
    updateTenantInURL
  };
}
